import { useEffect, useRef, useState, type CSSProperties } from 'react';
import styled from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import useProgressiveImg from 'hooks/use-progressive-image';
import Icon from 'components/global/Icon/Primary';
import type { ContentImage } from 'components/pdp/modules/types';

const ImagesWrapper = styled.div`
  --small-image: 8.875rem;

  height: fit-content;
  max-width: 38.5625rem;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr var(--small-image);
  grid-template-rows: repeat(2, auto);
  gap: 1rem;
  overflow: hidden;

  @media ${mqLargeMin} {
    --small-image: 13.25rem;
  }
`;

/** Thumbnail row only (matches bottom row of full grid; used below timeline on mobile). */
const ImagesOnlyWrapper = styled.div`
  --small-image: 8.875rem;

  height: fit-content;
  max-width: 38.5625rem;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr var(--small-image);
  grid-template-rows: auto;
  gap: 1rem;
  overflow: hidden;

  @media ${mqLargeMin} {
    --small-image: 13.25rem;
  }
`;

const VideoOnlyRoot = styled.div`
  width: 100%;
  max-width: 38.5625rem;
`;

const ImageWrapper = styled.div`
  overflow: hidden;
`;

const BenefitImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 358 / 202;
  cursor: pointer;

  @media ${mqLargeMin} {
    aspect-ratio: unset;
    height: 21.75rem;
  }

  video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }
`;

const VideoControls = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
  display: flex;
  gap: 0.5rem;
`;

const ControlButton = styled.button`
  box-sizing: border-box;
  height: 2.25rem;
  min-width: 4rem;
  padding: 0 0.875rem;
  border: none;
  border-radius: 9999px;
  background: var(--color-dark-opacity-20);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;

  &:focus-visible {
    outline: solid 2px #fff;
    outline-offset: 2px;
  }

  svg {
    display: block;
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }
`;

const CaptionOverlay = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  max-width: 90%;
  padding: 0.25rem 0.625rem;
  border-radius: 0.25rem;
  background: var(--color-dark-opacity-60);
  color: var(--color-light-opacity-100);
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
  pointer-events: none;

  @media ${mqLargeMin} {
    font-size: 16px;
  }
`;

const ProductBenefitImage = ({
  image,
  extraStyles = {},
}: {
  image: ContentImage;
  extraStyles?: CSSProperties;
}) => {
  const currentImage = useProgressiveImg({
    mediaItem: image,
    mediaSize: 'medium',
  });

  return (
    <BenefitImage
      ref={currentImage.ref}
      src={currentImage.src}
      alt={image.alt}
      style={{ ...extraStyles, ...currentImage.blurStyles }}
    />
  );
};

const getRowAndCol = (index) => {
  if (index === 0) {
    return {
      gridColumn: '1 / span 2',
      borderRadius: '2rem',
    };
  } else if (index === 1) {
    return {
      borderRadius: '1rem',
      maxHeight: 'var(--small-image)',
    };
  } else {
    return {
      borderRadius: '100%',
      maxHeight: 'var(--small-image)',
    };
  }
};

interface VttCue {
  start: number;
  end: number;
  text: string;
}

const parseVtt = (raw: string): VttCue[] => {
  const cues: VttCue[] = [];
  const blocks = raw.replace(/\r\n/g, '\n').split('\n\n');

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    const timeLine = lines.find((l) => l.includes('-->'));
    if (!timeLine) continue;

    const [startStr, endStr] = timeLine.split('-->').map((s) => s.trim());
    const toSeconds = (ts: string) => {
      const parts = ts.split(':');
      const [sec, ms] = (parts.pop() || '0').split('.');
      const mins = parseInt(parts.pop() || '0', 10);
      const hrs = parseInt(parts.pop() || '0', 10);
      return (
        hrs * 3600 +
        mins * 60 +
        parseInt(sec, 10) +
        parseInt(ms || '0', 10) / 1000
      );
    };

    const textStartIdx = lines.indexOf(timeLine) + 1;
    const text = lines.slice(textStartIdx).join(' ').trim();
    if (text) {
      cues.push({ start: toSeconds(startStr), end: toSeconds(endStr), text });
    }
  }
  return cues;
};

export type DSProductBenefitImagesVariant =
  | 'full'
  | 'video-only'
  | 'images-only';

interface DSProductBenefitImagesProps {
  images: ContentImage[];
  videoUrl?: string;
  videoCaptionsUrl?: string;
  variant: DSProductBenefitImagesVariant;
}

const DSProductBenefitImages = ({
  images,
  videoUrl,
  videoCaptionsUrl,
  variant,
}: DSProductBenefitImagesProps) => {
  const showVideo = variant !== 'images-only';
  const captionsUrl = showVideo ? videoCaptionsUrl : null;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [caption, setCaption] = useState('');
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const cuesRef = useRef<VttCue[]>([]);

  useEffect(() => {
    if (!captionsUrl) return;

    fetch(captionsUrl)
      .then((res) => res.text())
      .then((raw) => {
        cuesRef.current = parseVtt(raw);
      })
      .catch(() => {});
  }, [captionsUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !captionsUrl) return;

    const onTimeUpdate = () => {
      const t = video.currentTime;
      const activeCue = cuesRef.current.find((c) => t >= c.start && t <= c.end);
      setCaption(activeCue?.text || '');
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [captionsUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setIsPlaying(!video.paused);
    setIsMuted(video.muted);

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, []);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  };

  const videoBlock = (
    <VideoWrapper style={getRowAndCol(0)} onClick={togglePlay}>
      <video ref={videoRef} src={videoUrl} autoPlay muted loop playsInline />
      <VideoControls onClick={(e) => e.stopPropagation()}>
        <ControlButton
          type='button'
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <Icon name='volume-off' /> : <Icon name='volume-on' />}
        </ControlButton>
        <ControlButton
          type='button'
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Icon name='pause' /> : <Icon name='play' />}
        </ControlButton>
      </VideoControls>
      {caption && <CaptionOverlay>{caption}</CaptionOverlay>}
    </VideoWrapper>
  );

  if (variant === 'images-only') {
    return (
      <ImagesOnlyWrapper>
        {images?.slice(0, 2).map((image, imageIndex) => (
          <ImageWrapper
            key={`benefit-image-${image.mediumUrl}`}
            style={{
              gridColumn: imageIndex === 0 ? 1 : 2,
              gridRow: 1,
              ...getRowAndCol(imageIndex + 1),
            }}
          >
            <ProductBenefitImage image={image} />
          </ImageWrapper>
        ))}
      </ImagesOnlyWrapper>
    );
  }

  if (variant === 'video-only') {
    return <VideoOnlyRoot>{videoBlock}</VideoOnlyRoot>;
  }

  return (
    <ImagesWrapper>
      {videoBlock}
      {images?.slice(0, 2).map((image, imageIndex) => (
        <ImageWrapper
          key={`benefit-image-${image.mediumUrl}`}
          style={getRowAndCol(imageIndex + 1)}
        >
          <ProductBenefitImage image={image} />
        </ImageWrapper>
      ))}
    </ImagesWrapper>
  );
};

export default DSProductBenefitImages;
